import { menuItems, services } from "../config/catalog";
import type { AdminSession, CheckoutPayload, CheckoutResponse, MenuItem, Service } from "../types";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    credentials: "include",
    ...init
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getMenu(): Promise<MenuItem[]> {
  try {
    return await request<MenuItem[]>("/api/menu");
  } catch {
    return menuItems;
  }
}

export async function getServices(): Promise<Service[]> {
  try {
    return await request<Service[]>("/api/services");
  } catch {
    return services;
  }
}

export async function checkout(payload: CheckoutPayload): Promise<CheckoutResponse> {
  return request<CheckoutResponse>("/api/checkout", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function newsletter(email: string) {
  return request<{ ok: true }>("/api/newsletter", {
    method: "POST",
    body: JSON.stringify({ email })
  });
}

export async function adminLogin(password: string) {
  return request<AdminSession>("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ password })
  });
}

export async function adminSession() {
  return request<AdminSession>("/api/admin/session");
}

export async function adminLogout() {
  return request<{ ok: true }>("/api/admin/logout", { method: "POST" });
}

export async function adminUpsertMenu(item: MenuItem) {
  return request<MenuItem>("/api/admin/menu", { method: "PUT", body: JSON.stringify(item) });
}

export async function adminUpsertService(service: Service) {
  return request<Service>("/api/admin/services", { method: "PUT", body: JSON.stringify(service) });
}

export async function adminOrders() {
  return request<Array<{ id: string; customerEmail: string; status: string; totalCents: number; createdAt: string }>>(
    "/api/admin/orders"
  );
}

export async function adminUploadMedia(file: File) {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch("/api/admin/media", {
    method: "POST",
    body: form,
    credentials: "include"
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Upload failed with ${response.status}`);
  }
  return response.json() as Promise<{ url: string }>;
}
